import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import config from '../config';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import { useTheme } from '../theme/useTheme';
import { previewInvite, InvitePreview } from './chat/teamService';
import { setPendingTeamWelcome, setTeamJoinError } from './chat/teamWelcome';

// Obsidian-flavoured Tailwind fragments: jade accent #5BD6A9, graphite
// neutrals, 10px fields on a 14px card, so the invited teammate's first screen
// looks like the app they are about to enter.
//
// The palette is driven by the IN-APP theme (useTheme reads localStorage
// 'theme', falling back to prefers-color-scheme when nothing is stored), not by
// Tailwind's dark-variant prefix, which follows the OS only. That mismatch was
// visible: a user on app theme light with an OS in dark mode got a dark /login
// in front of a light app. Every fragment below is therefore a function of that
// flag; keep the class names as whole literals so Tailwind's scanner sees them.
const labelClass = (isDark: boolean) =>
  'block text-sm font-medium mb-1.5 ' +
  (isDark ? 'text-[#F2F3F5]' : 'text-[#16181C]');
const inputClass = (isDark: boolean) =>
  'w-full px-3 py-2.5 rounded-[10px] border ' +
  'focus:outline-none focus:border-[#5BD6A9] focus:ring-[3px] focus:ring-[#5BD6A9]/20 ' +
  'transition-colors disabled:opacity-60 ' +
  (isDark
    ? 'bg-white/[0.05] border-white/[0.12] text-[#F2F3F5] placeholder-[#797F88]'
    : 'bg-white border-black/10 text-[#16181C] placeholder-[#6E747D]');
const primaryClass = (isDark: boolean) =>
  'w-full rounded-[10px] py-2.5 font-semibold text-sm bg-[#5BD6A9] text-[#06251C] ' +
  'hover:bg-[#6FE0B8] focus-visible:outline-none focus-visible:ring-[3px] ' +
  'focus-visible:ring-[#5BD6A9]/30 disabled:cursor-not-allowed transition-colors ' +
  (isDark
    ? 'disabled:bg-white/[0.06] disabled:text-[#797F88]'
    : 'disabled:bg-black/[0.06] disabled:text-[#6E747D]');
const secondaryClass = (isDark: boolean) =>
  'w-full rounded-[10px] py-2.5 font-medium text-sm border ' +
  'focus-visible:outline-none focus-visible:ring-[3px] ' +
  'focus-visible:ring-[#5BD6A9]/30 transition-colors ' +
  (isDark
    ? 'border-white/[0.12] text-[#F2F3F5] hover:bg-white/[0.06]'
    : 'border-black/10 text-[#16181C] hover:bg-black/[0.04]');
const pageClass = (isDark: boolean) =>
  'min-h-screen flex items-center justify-center px-4 ' +
  (isDark ? 'bg-[#0A0B0D]' : 'bg-[#F7F8FA]');
const cardClass = (isDark: boolean) =>
  'max-w-md w-full border rounded-[14px] shadow-lg p-8 ' +
  (isDark ? 'bg-[#101216] border-white/[0.08]' : 'bg-white border-black/[0.08]');
// Secondary body copy ("Belum punya akun?", the freeze notice).
const mutedClass = (isDark: boolean) => (isDark ? 'text-[#9FA4AD]' : 'text-[#565A62]');
// Jade text that stays legible on the light card (invite banner, dev OTP hint).
const accentTextClass = (isDark: boolean) => (isDark ? 'text-[#5BD6A9]' : 'text-[#0E9C6C]');
const hairlineClass = (isDark: boolean) => (isDark ? 'border-white/[0.08]' : 'border-black/[0.08]');
const quietButtonClass = (isDark: boolean) =>
  'w-full text-sm transition-colors py-2 ' +
  (isDark ? 'text-[#9FA4AD] hover:text-[#F2F3F5]' : 'text-[#565A62] hover:text-[#16181C]');
const footerClass = (isDark: boolean) =>
  'mt-6 text-xs text-center leading-relaxed ' +
  (isDark ? 'text-[#797F88]' : 'text-[#6E747D]');
const footerLinkClass = (isDark: boolean) =>
  'underline transition-colors ' +
  (isDark ? 'text-[#9FA4AD] hover:text-[#F2F3F5]' : 'text-[#565A62] hover:text-[#16181C]');
const errorClass = (isDark: boolean) =>
  'text-sm rounded-[10px] p-3 border ' +
  (isDark
    ? 'bg-red-900/30 text-red-400 border-red-900/60'
    : 'bg-red-50 text-red-600 border-red-200');
// Amber, not red: a dead invite link is not the visitor's mistake, and the
// login form below it still works perfectly well.
const noticeClass = (isDark: boolean) =>
  'mb-6 rounded-[10px] border px-4 py-3 text-center text-sm leading-relaxed ' +
  (isDark
    ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
    : 'border-amber-500/40 bg-amber-50 text-amber-700');

const Login = () => {
  const [searchParams] = useSearchParams();
  // Team invite token from the emailed link (/login?invite=TOKEN). A valid one
  // bypasses the signup freeze server-side, so the UI must keep the signup door
  // open whenever it is present.
  const inviteToken = searchParams.get('invite');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [devOtp, setDevOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Calm informational banner (jade, not error-red): why the form just
  // changed under the user, e.g. the login -> signup auto-switch.
  const [notice, setNotice] = useState('');
  const [isSignUp, setIsSignUp] = useState(
    searchParams.get('signup') === 'true' || !!searchParams.get('invite')
  );
  const [phoneNumber, setPhoneNumber] = useState('');
  const [companyName, setCompanyName] = useState('');
  // Same source of truth as the rest of the app: stored 'theme', with
  // prefers-color-scheme only as the first-visit fallback.
  const { theme } = useTheme(false, false);
  const isDark = theme === 'dark';
  // null while unknown; the backend is the single source of truth here so the
  // UI never offers a door the API has locked.
  const [signupsOpen, setSignupsOpen] = useState<boolean | null>(null);
  const [waitlistUrl, setWaitlistUrl] = useState('https://powerai.id/waitlist');
  // What the public preview endpoint says about the token in the URL. null =
  // still asking (or no token at all). An invalid link must NOT hold the signup
  // door open, otherwise the visitor fills in the whole form and is refused by
  // the freeze guard with a message that explains nothing.
  const [invitePreview, setInvitePreview] = useState<InvitePreview | null>(null);
  const inviteDead = !!inviteToken && invitePreview?.valid === false;
  // "The token may still work": unknown counts as usable, so a slow or missing
  // preview never locks someone out of a genuinely valid invite.
  const inviteUsable = !!inviteToken && !inviteDead;
  const navigate = useNavigate();

  // Who invited whom, before anyone signs in. Unauthenticated by design: the
  // token is the secret, and the invitee has no account yet.
  useEffect(() => {
    if (!inviteToken) {
      setInvitePreview(null);
      return;
    }
    let cancelled = false;
    previewInvite(inviteToken).then((preview) => {
      if (!cancelled) setInvitePreview(preview);
    });
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  // A dead invite behaves exactly like arriving with no token at all: the
  // forced signup mode is undone, unless the visitor asked for it themselves
  // with ?signup=true. Pushing someone into a signup the freeze will refuse is
  // the worst possible answer to a stale link.
  useEffect(() => {
    if (inviteDead && searchParams.get('signup') !== 'true') setIsSignUp(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteDead]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${config.apiUrl}/auth/intake-status`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        // A 404 (backend not yet deployed) or any error body: fail closed
        // rather than hang on "unknown" forever.
        if (!data) {
          setSignupsOpen(false);
          // An invite token is its own door; keep signup mode even when the
          // status endpoint is unreachable.
          if (!inviteToken) setIsSignUp(false);
          return;
        }
        setSignupsOpen(data.signups_open);
        if (data.waitlist_url) setWaitlistUrl(data.waitlist_url);
        if (!data.signups_open && !inviteToken) setIsSignUp(false);
      })
      .catch(() => {
        // Fail closed. Offering a signup door we cannot verify is open just
        // sends people into a 403.
        if (!cancelled) setSignupsOpen(false);
      });
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  const handleInitialRequest = async () => {
    try {
      setLoading(true);
      setError('');
      setNotice('');

      // Track login/signup attempt
      mixpanel.track(isSignUp ? 'Signup Initiated' : 'Login Initiated', {
        email: email,
        has_phone: !!phoneNumber,
        has_company: !!companyName
      });

      const endpoint = isSignUp ? 'signup' : 'login';
      const payload = isSignUp
        ? {
            email,
            phone_number: phoneNumber || undefined,
            company_name: companyName || undefined,
            // A valid invite token is what lets this signup through while
            // SIGNUPS_OPEN is false.
            invite_token: inviteToken || undefined
          }
        : { email };

      const response = await fetch(`${config.apiUrl}/auth/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json();
        const detail = data.detail;
        // The freeze returns a structured detail object rather than a string.
        if (detail && typeof detail === 'object' && detail.code === 'signups_closed') {
          setSignupsOpen(false);
          // With an invite token in hand, a signups_closed answer means the
          // token itself is no longer valid; keep the form and say so.
          if (!inviteToken) setIsSignUp(false);
          if (detail.waitlist_url) setWaitlistUrl(detail.waitlist_url);
          throw new Error(
            inviteToken
              ? 'Undangan ini sudah tidak berlaku. Minta pemilik tim mengirim undangan baru.'
              : detail.message || 'Pendaftaran sedang ditutup.'
          );
        }

        const detailText = typeof detail === 'string' ? detail : detail?.message || '';

        // If user doesn't exist during login, switch to signup mode -- but not
        // while signups are closed, or we would strand them on a locked door.
        if (!isSignUp && detailText.toLowerCase().includes('user not found')) {
          if (signupsOpen === false && !inviteUsable) {
            throw new Error(
              'Email ini belum terdaftar, dan pendaftaran akun baru sedang ditutup sementara.'
            );
          }
          setIsSignUp(true);
          // SAY WHY (2026-08-03, after a real complaint "pencet sudah punya
          // akun, balik lagi ke halaman daftar"): the silent flip read as a
          // bug. Since the clean-DB relaunch every pre-relaunch account hits
          // this branch, so the message also explains the re-registration.
          setNotice(
            `${email} belum terdaftar di sistem kami yang baru. Kalau kamu pernah ` +
            'punya akun Power AI sebelumnya: kami baru pindah sistem, jadi semua ' +
            'akun perlu didaftarkan ulang. Formulir daftarnya sudah disiapkan di bawah.'
          );
          // Track auto-switch to signup
          mixpanel.track('Auto Switch to Signup', {
            email: email
          });
          return; // Exit early without showing error
        }
        throw new Error(detailText || 'Permintaan gagal diproses. Coba lagi.');
      }

      // Local development only: the backend includes the code when
      // DEV_SHOW_OTP is on (double-gated server-side), since the local
      // Brevo key cannot send real email.
      try {
        const okData = await response.json();
        setDevOtp(typeof okData.dev_otp === 'string' ? okData.dev_otp : '');
      } catch {
        setDevOtp('');
      }

      setOtpSent(true);
    } catch (err: any) {
      // Track error
      mixpanel.track('Auth Error', {
        type: isSignUp ? 'signup' : 'login',
        error: err.message
      });
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Track OTP verification attempt
      mixpanel.track('OTP Verification Attempted', {
        email: email
      });

      const response = await fetch(`${config.apiUrl}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The invite token rides along so the backend can attach team
        // membership the moment the account is verified (idempotent).
        body: JSON.stringify({ email, code: otp, invite_token: inviteToken || undefined })
      });

      if (!response.ok) {
        throw new Error('Kode verifikasi salah.');
      }

      const data = await response.json();
      localStorage.setItem('accessToken', data.access_token);
      localStorage.setItem('tokenType', data.token_type);

      // Set cookie for cross-domain authentication
      // Set it on the top-level domain so it's accessible by all subdomains
      Cookies.set('powerai_auth', data.access_token, {
        expires: 300, // Cookie expires in 7 days
        domain: '.powerai.id', // Notice the dot prefix to include all subdomains
        secure: true, // Only sent over HTTPS
        sameSite: 'lax' // Allows the cookie to be sent in cross-site navigation
      });

      // Team invite: attach membership explicitly. verify-otp already does this
      // for brand new accounts, but an EXISTING user clicking an invite link
      // logs in rather than signs up, so this is their only join path. The
      // endpoint is idempotent for the same team; a 409 "already_in_team" is a
      // no-op we swallow. Deliberately non-fatal: a bad invite must never block
      // a successful login. Runs before /auth/me so the effective status the
      // switch below routes on already accounts for pool coverage.
      if (inviteToken) {
        try {
          const joinResponse = await fetch(`${config.apiUrl}/teams/join`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${data.access_token}`
            },
            body: JSON.stringify({ token: inviteToken })
          });
          if (!joinResponse.ok) {
            // 409 is normally 'already_in_team', a genuine no-op. The one 409
            // that is NOT is 'team_full' (the team filled up between the invite
            // mail and this click): the person is logged in but teamless, so
            // the backend's Indonesian sentence rides along to the team panel
            // instead of vanishing into the console.
            let joinCode = '';
            let joinMessage = '';
            try {
              const joinBody = await joinResponse.clone().json();
              const d = joinBody?.detail;
              if (d && typeof d === 'object') {
                joinCode = typeof d.code === 'string' ? d.code : '';
                joinMessage = typeof d.message === 'string' ? d.message : '';
              }
            } catch {
              /* not JSON - nothing to relay */
            }
            if (joinCode === 'team_full' && joinMessage) {
              setTeamJoinError(joinMessage);
            } else if (joinResponse.status !== 409) {
              console.warn('Team join failed after verification:', joinResponse.status);
            }
          }

          // Arm the one-time welcome card in the chat hero. Both join paths end
          // here: a brand new account (verify-otp already attached membership,
          // this call is the idempotent replay) and an existing user clicking
          // an invite link. The join response IS the /teams/me shape; when it
          // did not answer with one (409 from a different team, or a transient
          // failure after verify-otp already attached), fall back to reading
          // /teams/me so the card still knows which team to name.
          let team: any = null;
          if (joinResponse.ok) {
            try {
              team = (await joinResponse.json())?.team ?? null;
            } catch {
              team = null;
            }
          }
          if (!team) {
            try {
              const meResponse = await fetch(`${config.apiUrl}/teams/me`, {
                headers: { 'Authorization': `Bearer ${data.access_token}` }
              });
              if (meResponse.ok) team = (await meResponse.json())?.team ?? null;
            } catch {
              team = null;
            }
          }
          if (team && typeof team.id === 'number') {
            setPendingTeamWelcome(team.id, String(team.name || ''));
          }
        } catch (joinError) {
          console.warn('Team join request errored after verification:', joinError);
        }
      }

      // After verification, check user status
      const userResponse = await fetch(`${config.apiUrl}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${data.access_token}`
        }
      });
      const userData = await userResponse.json();
      
      // Convert status to uppercase for consistency
      const status = userData.status.toUpperCase();

      // Redirect based on user status
      switch (status) {
        case 'ACTIVE':
          navigate('/'); // This will now go to the Chat component
          break;
        case 'PENDING':
        case 'VERIFIED':
          navigate('/'); // Can access chat but need to complete subscription
          break;
        case 'LIMITED':
        case 'EXPIRED':
          navigate('/subscription'); // Redirect LIMITED/EXPIRED users to subscription page
          break;
        case 'LITE':
          navigate('/'); // Allow access but with limited features
          break;
        case 'INACTIVE':
          navigate('/subscription'); // Subscription expired/cancelled
          setError('Langgananmu sudah berakhir. Perpanjang dulu untuk melanjutkan.');
          break;
        default:
          console.log('Unexpected status:', status);
          setError(`Status akun tidak dikenali (${status}). Hubungi dukungan ya.`);
      }

      mixpanel.identify(userData.id); // Set user identity
      mixpanel.people.set({
        $email: email,
        $name: userData.name,
        status: userData.status,
        signup_date: new Date().toISOString()
      });
      
      mixpanel.track('Login Success', {
        status: userData.status,
        method: 'otp'
      });
    } catch (err) {
      mixpanel.track('OTP Verification Failed', {
        email: email
      });
      setError('Kode verifikasi salah. Coba lagi ya.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (!otpSent) {
        handleInitialRequest();
      } else {
        handleVerifyOTP();
      }
    }
  };

  return (
    <div className={pageClass(isDark)}>
      <div className={cardClass(isDark)}>
        {/* Logo */}
        <div className="w-20 h-20 mx-auto mb-6">
          <img src={isDark ? "/logo-only200-removebg-inverted.png" : "/logo-only200-removebg.png"} alt="Power AI" className="w-full h-full rounded-[10px]" />
        </div>

        <p className={`text-center mb-6 ${mutedClass(isDark)}`}>
          {isSignUp ? 'Buat akun Power AI' : 'Masuk ke akun Power AI'}
        </p>

        {/* Invite context, straight from the public preview endpoint. Shown even
            while general signups are closed, because a valid invite token is the
            deliberate door through the freeze. A dead link says so in amber and
            stops pretending the signup door is open.
            Balanced wrap so the last line never drops a lone orphan word. */}
        {inviteToken && invitePreview?.valid && (
          <div
            className={`mb-6 rounded-[10px] border border-[#5BD6A9]/40 bg-[#5BD6A9]/10 px-4 py-3 text-center text-sm leading-relaxed ${accentTextClass(isDark)}`}
            style={{ ['textWrap' as any]: 'balance' }}
          >
            <div>
              {invitePreview.team_name
                ? `Kamu diundang bergabung ke tim ${invitePreview.team_name} di Power AI.`
                : 'Kamu diundang bergabung ke tim di Power AI.'}
            </div>
            {invitePreview.email && (
              <div className={`mt-1 text-xs ${mutedClass(isDark)}`}>
                Undangan untuk {invitePreview.email}
              </div>
            )}
          </div>
        )}
        {inviteDead && (
          <div
            className={noticeClass(isDark)}
            style={{ ['textWrap' as any]: 'balance' }}
          >
            Tautan undangan ini tidak valid atau sudah kedaluwarsa. Minta pemilik
            tim mengirim undangan baru.
          </div>
        )}

        {/* Form */}
        <div className="space-y-4">
          {!otpSent ? (
            <>
              <div>
                <label className={labelClass(isDark)}>
                  Alamat email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className={inputClass(isDark)}
                  placeholder="nama@email.com"
                  disabled={loading}
                />
              </div>

              {isSignUp && (
                <>
                  <div>
                    <label className={labelClass(isDark)}>
                      Nomor HP (opsional)
                    </label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className={inputClass(isDark)}
                      placeholder="+6281223..."
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className={labelClass(isDark)}>
                      Nama perusahaan (opsional)
                    </label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className={inputClass(isDark)}
                      placeholder="PT Indonesia Maju"
                      disabled={loading}
                    />
                  </div>
                </>
              )}

              <button
                onClick={handleInitialRequest}
                disabled={loading || !email}
                className={primaryClass(isDark)}
              >
                {loading ? 'Mengirim…' : (isSignUp ? 'Daftar' : 'Lanjut dengan email')}
              </button>

              {/* While the status is still unknown we show neither door. An
                  invite token skips the freeze notice and falls through to the
                  normal toggles: the token bypasses the signup guard, and an
                  invitee who already has an account still needs the login door. */}
              {signupsOpen === null ? null : signupsOpen === false && !inviteUsable ? (
                <div className={`text-center border-t pt-4 ${hairlineClass(isDark)}`}>
                  <p className={`mb-3 text-sm leading-relaxed ${mutedClass(isDark)}`}>
                    Pendaftaran akun baru sedang ditutup sementara karena kami sedang
                    membenahi Power AI. Akun yang sudah ada tetap bisa masuk seperti biasa.
                  </p>
                  <a href={waitlistUrl} className={`${secondaryClass(isDark)} block`}>
                    Kabari aku saat dibuka lagi
                  </a>
                </div>
              ) : !isSignUp ? (
                <div className="text-center">
                  <p className={`mb-2 text-sm ${mutedClass(isDark)}`}>Belum punya akun?</p>
                  <button type="button" onClick={() => { setNotice(''); setError(''); setIsSignUp(true); }} className={secondaryClass(isDark)}>
                    Daftar di sini
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <p className={`mb-2 text-sm ${mutedClass(isDark)}`}>Sudah punya akun?</p>
                  <button type="button" onClick={() => { setNotice(''); setError(''); setIsSignUp(false); }} className={secondaryClass(isDark)}>
                    Masuk di sini
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <label className={labelClass(isDark)}>
                  Kode verifikasi
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className={`${inputClass(isDark)} text-center tracking-[0.4em]`}
                  placeholder="••••••"
                  maxLength={6}
                  disabled={loading}
                />
                {devOtp && (
                  <button
                    type="button"
                    onClick={() => setOtp(devOtp)}
                    className={`mt-2 w-full text-center text-xs font-mono rounded-[10px] py-1.5 border border-dashed border-[#5BD6A9]/50 bg-[#5BD6A9]/10 hover:bg-[#5BD6A9]/20 transition-colors ${accentTextClass(isDark)}`}
                    title="Hanya tampil di mode development"
                  >
                    DEV · kode: {devOtp} (klik untuk isi)
                  </button>
                )}
              </div>
              <button
                onClick={handleVerifyOTP}
                disabled={loading || !otp}
                className={primaryClass(isDark)}
              >
                {loading ? 'Memverifikasi…' : 'Verifikasi dan masuk'}
              </button>
              <button
                onClick={() => setOtpSent(false)}
                className={quietButtonClass(isDark)}
              >
                Pakai email lain
              </button>
            </>
          )}

          {notice && (
            <div
              className="text-sm rounded-[10px] px-4 py-3 leading-relaxed"
              style={{
                border: '1px solid rgba(91,214,169,0.45)',
                background: 'rgba(91,214,169,0.10)',
                color: isDark ? '#BFE9D9' : '#116149',
              }}
            >
              {notice}
            </div>
          )}
          {error && (
            <div className={errorClass(isDark)}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <p
          className={footerClass(isDark)}
          style={{ ['textWrap' as any]: 'balance' }}
        >
          Dengan melanjutkan, kamu menyetujui{' '}
          <a href="https://powerai.id/terms" className={footerLinkClass(isDark)}>
            Ketentuan Layanan
          </a>{' '}
          dan{' '}
          <a href="https://powerai.id/privacy" className={footerLinkClass(isDark)}>
            Kebijakan Privasi
          </a>{' '}
          kami.
        </p>
      </div>
    </div>
  );
};

export default Login;